import React, { useEffect, useState } from 'react';
import { Shield, MapPin, PauseCircle, PlayCircle } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';

const PAUSE_OPTIONS = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: '8 hours', hours: 8 },
  { label: '24 hours', hours: 24 },
];

export const LocationPrivacySettings: React.FC = () => {
  const { locationPrefs, updateSharing, pauseSharing } = useLocation();
  const [countdown, setCountdown] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [pausing, setPausing] = useState(false);

  // Update countdown every minute when paused
  useEffect(() => {
    if (!locationPrefs?.pausedUntil) {
      setCountdown(null);
      return;
    }
    const updateCountdown = () => {
      const remaining = locationPrefs.pausedUntil!.toMillis() - Date.now();
      if (remaining <= 0) {
        setCountdown(null);
        return;
      }
      const resumeDate = new Date(locationPrefs.pausedUntil!.toMillis());
      setCountdown(
        resumeDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60_000);
    return () => clearInterval(interval);
  }, [locationPrefs?.pausedUntil]);

  const isPaused =
    locationPrefs?.pausedUntil !== null &&
    locationPrefs?.pausedUntil !== undefined &&
    locationPrefs.pausedUntil.toMillis() > Date.now();

  const isSharing = locationPrefs?.sharing ?? false;

  const handleToggle = async () => {
    setToggling(true);
    try {
      await updateSharing(!isSharing);
    } finally {
      setToggling(false);
    }
  };

  const handlePause = async (hours: number) => {
    setPausing(true);
    try {
      await pauseSharing(hours);
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    setPausing(true);
    try {
      await pauseSharing(0);
    } finally {
      setPausing(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-brand" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-900 dark:text-white">Location Privacy</h1>
          <p className="text-xs text-slate-500">Control who sees your location</p>
        </div>
      </div>

      {/* Privacy disclaimer */}
      <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl">
        <Shield className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
          Only household members can see your location. Household admins{' '}
          <strong>cannot</strong> override your privacy settings.
        </p>
      </div>

      {/* Master sharing toggle */}
      <div className="flex items-center justify-between px-4 py-4 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl">
        <div>
          <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Share my location</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {isSharing ? 'Visible to household members' : 'Hidden from household'}
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={toggling}
          className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
            isSharing ? 'bg-brand' : 'bg-slate-300 dark:bg-slate-600'
          } disabled:opacity-50`}
          aria-label="Toggle location sharing"
        >
          <span
            className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
              isSharing ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Pause controls — only when sharing is on */}
      {isSharing && (
        <div className="space-y-3">
          <h2 className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Temporarily Pause
          </h2>

          {isPaused ? (
            <div className="flex items-center justify-between px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl">
              <div className="flex items-center gap-2">
                <PauseCircle className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Paused</p>
                  {countdown && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                      Resumes at {countdown}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleResume}
                disabled={pausing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-xl text-xs font-bold hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                <PlayCircle className="w-3.5 h-3.5" /> Resume
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {PAUSE_OPTIONS.map(({ label, hours }) => (
                <button
                  key={hours}
                  onClick={() => handlePause(hours)}
                  disabled={pausing}
                  className="px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-semibold text-slate-700 dark:text-slate-300 hover:border-brand hover:text-brand transition-colors disabled:opacity-50"
                >
                  Pause {label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info note */}
      <p className="text-xs text-slate-400 dark:text-slate-500 text-center leading-relaxed">
        Location is shared only while the app is open. History is automatically deleted after 24 hours.
        Web GPS accuracy may be reduced compared to mobile devices.
      </p>
    </div>
  );
};
